import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import es from "./locales/es";
import de from "./locales/de";
import fr from "./locales/fr";
import zh from "./locales/zh";
import ja from "./locales/ja";
import ru from "./locales/ru";
import pt from "./locales/pt";
import tr from "./locales/tr";
import { SRV_EN, SRV_FA, matchServer, renderKey } from "./server";

export const LANGUAGES = [
  { code: "en", label: "English", dir: "ltr" },
  { code: "fa", label: "فارسی", dir: "rtl" },
  { code: "es", label: "Español", dir: "ltr" },
  { code: "de", label: "Deutsch", dir: "ltr" },
  { code: "fr", label: "Français", dir: "ltr" },
  { code: "zh", label: "中文", dir: "ltr" },
  { code: "ja", label: "日本語", dir: "ltr" },
  { code: "ru", label: "Русский", dir: "ltr" },
  { code: "pt", label: "Português", dir: "ltr" },
  { code: "tr", label: "Türkçe", dir: "ltr" },
];

const en = {
  app_name: "FIREBIRD AI",
  tagline: "AI Crypto & NFT Trading",
  login: "Log In",
  register: "Sign Up",
  logout: "Log Out",
  email: "Email",
  password: "Password",
  name: "Name",
  welcome_back: "Welcome back, trader",
  create_account: "Create your account",
  no_account: "No account yet?",
  have_account: "Already have an account?",
  account_created: "Account created — $100 demo ready",
  loading: "Loading…",
  demo_mode: "Demo Mode",
  demo_balance: "Demo Balance",
  nav_dashboard: "Dashboard",
  nav_charts: "Live Charts",
  nav_signals: "AI Signals",
  nav_autotrade: "Auto-Trade",
  nav_manual: "Manual Trade",
  nav_scanner: "Memecoin Scanner",
  nav_backtest: "5×5 Backtest",
  nav_positions: "Positions & Reports",
  nav_nft: "NFT Collection",
  nav_assistant: "AI Assistant",
  nav_exchange: "Exchange",
  nav_settings: "Settings",
  nav_subscription: "Premium Plan",
  subscription_subtitle: "Unlock premium trading intelligence — pay in crypto.",
  current_plan: "Current Plan",
  premium: "Premium",
  free_plan: "Free Plan",
  renews_on: "Renews on",
  active: "Active",
  best_value: "Best Value",
  plan_monthly: "Monthly",
  plan_annual: "Annual",
  plan_test: "Test",
  days: "days",
  pay_with_crypto: "Pay with crypto",
  opening_checkout: "Opening checkout…",
  gateway_not_ready: "The crypto payment gateway is not configured yet. Premium checkout will be enabled once the NOWPayments API keys are added.",
  premium_only: "Premium only",
  premium_gate_desc: "This feature is part of FireBird Premium. Upgrade to unlock full AI signals, auto-trade and the opportunity scanner.",
  go_premium: "Go Premium",
  billing_history: "Billing History",
  no_invoices: "No invoices yet. Your crypto payments will appear here.",
  date: "Date",
  plan_col: "Plan",
  amount_col: "Amount",
  status_col: "Status",
  view_invoice: "View invoice",
  check_status: "Check payment status",
  checking: "Checking…",
  payment_still_pending: "Payment not confirmed yet. We'll keep checking automatically.",
  status_paid: "Paid",
  status_created: "Pending",
  status_partially_paid: "Partially paid",
  status_expired: "Expired",
  status_failed: "Failed",
  status_refunded: "Refunded",
  status_cancelled: "Cancelled",
  status_canceled: "Cancelled",
  payment_processing: "Payment detected — activating your premium plan…",
  payment_success: "Premium activated. Welcome aboard!",
  payment_cancelled: "Payment cancelled.",
  payment_error: "Could not start checkout. Please try again.",
  popup_blocked: "Checkout is ready. If a new tab didn't open, use the link below.",
  checkout_opened_hint: "Checkout opened in a new tab. Didn't see it?",
  open_checkout: "Open checkout",
  pay_currency_label: "Pay with",
  pay_currency_hint: "We accept USDC on Solana and BNB on BSC. Your chosen asset is locked at checkout.",
  feat_signals: "Full AI signals (Crypto, Shitcoins, NFT)",
  feat_autotrade: "Auto-Trade with risk management",
  feat_scanner: "Memecoin & opportunity scanner",
  feat_alerts: "Smart email alerts",
  feat_priority: "Priority signal delivery",
  feat_save: "2 months free vs monthly",
  feat_test_quick: "One-time $1 test — verifies the live payment flow",
  demo_account_settings: "Demo Account Settings",
  demo_settings_desc: "Set a custom demo balance and reset your paper-trading account. This affects demo data only — it never touches your subscription or any live trading.",
  custom_demo_balance: "Custom Demo Balance (USDC)",
  demo_min_hint: "Default 500 · minimum 100",
  reset_demo_account: "Reset Demo Account",
  reset_demo_confirm_title: "Reset demo account?",
  reset_demo_confirm_desc: "This sets your demo balance to ${bal}, deletes all demo trades and P/L history, and clears drawdown, halt and cooldown status. This cannot be undone. Your subscription and live data are not affected.",
  demo_reset_done: "Demo account reset — balance set to ${bal}",
  demo_min_balance_error: "Minimum demo balance is 100",
  nav_guide: "Beginner Guide",
  owner_console: "Owner Console",
  total_value: "Total Value",
  cash: "Cash",
  holdings: "Holdings",
  unrealized_pnl: "Unrealized PnL",
  top_signals: "Top AI Signals",
  market_overview: "Market Overview",
  whale_activity: "Whale & Smart Money",
  market_pulse: "Market Pulse",
  news_up: "{name} surges {pct}% in 24h", news_down: "{name} drops {pct}% in 24h",
  kind_cex: "CEX", kind_iranian_cex: "Iranian CEX", kind_dex: "DEX",
  price: "Price",
  change_24h: "24h",
  score: "Score",
  action: "Action",
  buy: "Buy",
  sell: "Sell",
  hold: "Hold",
  timeframe: "Timeframe",
  symbol: "Symbol",
  amount_usd: "Amount (USD)",
  open_position: "Open Position",
  close: "Close",
  no_positions: "No open positions",
  entry: "Entry",
  current: "Current",
  exit: "Exit",
  reason: "Reason",
  pnl: "PnL",
  win_rate: "Win Rate",
  total_trades: "Total Trades",
  total_pnl: "Total PnL",
  wl: "W / L",
  open_label: "Open",
  run_backtest: "Run Backtest",
  running: "Running…",
  bt_subtitle: "Strategy tested across 5 historical windows per timeframe.",
  window_results: "Window Results",
  window: "Window",
  trades: "Trades",
  return_label: "Return",
  max_drawdown: "Max Drawdown",
  auto_trade_on: "Auto-Trade Active",
  auto_trade_off: "Auto-Trade Off",
  enable_autotrade: "Enable Auto-Trade (Demo)",
  reset_demo: "Reset Demo ($100)",
  activity_log: "Activity Log",
  waiting_signals: "Waiting for signals…",
  enable_to_start: "Enable auto-trade to start",
  reauthorize_bot: "Re-authorize Bot",
  leverage: "Leverage",
  high_leverage_warning: "High leverage — you can lose your full margin (liquidation).",
  bot_description: "The bot scans all coins across 15m / 1h / 4h, enters the best signal (2:1 reward:risk, stop required), and manages positions with trailing take-profit in demo mode.",
  rule_rr: "Reward:Risk 2:1 (stop required)",
  rule_max_concurrent: "Max 2 concurrent auto-trades",
  rule_leverage: "Auto leverage ≤ 10x · Manual ≤ 50x",
  rule_circuit: "Circuit-breaker at 20% drawdown",
  rule_halt: "Halt after 5 losses",
  rule_scan: "Scans all coins & timeframes",
  rule_entry: "Entry score ≥ {score} on {tfs}",
  rule_cooldown: "Cooldown {min} min after {losses} losses",
  risk_status: "Risk Status",
  risk_mode_active: "Active",
  risk_mode_halted: "Halted",
  consecutive_losses_label: "Consecutive Losses",
  drawdown_label: "Drawdown",
  open_positions_label: "Open Positions",
  max_concurrent_reached: "Max concurrent trades reached.",
  bot_reauthorized: "Bot re-authorized. You can enable auto-trade again.",
  cloud_trading: "Cloud Trading",
  enable_cloud: "Enable Cloud Trading",
  disable_cloud: "Disable Cloud Trading",
  cloud_status: "Cloud Status",
  cloud_on: "Running 24/7",
  cloud_off: "Off",
  cloud_desc: "The bot runs on our servers 24/7, even when your device is off.",
  notifications: "Notifications",
  no_notifications: "No notifications yet",
  mark_all_read: "Mark all read",
  connect_exchange: "Connect Exchange",
  api_key: "API Key",
  api_secret: "API Secret",
  api_key_optional: "API Key (optional for public prices)",
  connect: "Connect",
  test: "Test",
  remove: "Remove",
  verified: "Verified",
  not_verified: "Not Verified",
  connected_exchanges: "Connected Exchanges",
  aggregated_balances: "Balances · All Exchanges",
  aggregated_prices: "Live Prices · All Exchanges",
  no_connections: "No exchanges connected yet.",
  connect_wallet: "Connect Wallet",
  wallet_connected: "Wallet Connected",
  wallet_note: "Non-custodial — we never store your private keys.",
  wallet_security: "Wallet & Security",
  wallet_never_ask: "We never ask for your private key or seed phrase.",
  disconnect_wallet: "Disconnect Wallet",
  switch_chain: "Switch Chain",
  native_balance: "Native Balance",
  wallet_chain: "Network",
  dex_onchain: "DEX (On-chain)",
  sign_in_wallet: "Sign in Wallet",
  waiting_signature: "Waiting for wallet signature…",
  dex_demo_note: "You sign in your own wallet — keys never leave your device.",
  view_on_explorer: "View on Explorer",
  wallet_required_dex: "Connect your wallet to trade on-chain.",
  tx_pending: "Transaction pending…",
  tx_confirmed: "Transaction confirmed",
  tx_failed: "Transaction failed",
  connected_label: "Connected",
  not_connected_label: "Not connected",
  copy_address: "Copy address",
  readonly_warning: "Create your API key with READ-ONLY permissions. Disable Withdraw and Trade.",
  cex_security_notice: "We NEVER store your private keys or request Withdraw permissions.",
  api_passphrase_optional: "Passphrase (optional)",
  read_only_label: "Read-Only",
  how_to_create_key: "How to create a read-only key",
  connected_wallets: "Connected Wallets",
  no_wallet_connected: "No wallet connected.",
  no_exchange_linked: "No exchange linked.",
  smart_contract_allowances: "Smart Contract Allowances",
  no_allowances: "No active allowances.",
  revoke: "Revoke",
  max_spend: "Max spend",
  link_readonly_hint: "Use the Connect Exchange panel to link a read-only key.",
  vpn_notice: "This marketplace may require a VPN for users in some regions.",
  ir_friendly: "IR-friendly",
  best_price: "Best Price",
  marketplace_listings: "Marketplace Listings",
  tab_opps: "Opportunities",
  opportunities: "Opportunities",
  high_opportunity: "High Opportunity",
  alerts_triggered_today: "alerts triggered today",
  score_range: "Score range",
  timeframe_label: "Timeframe",
  direction_label: "Direction",
  risk_disclaimer_title: "Risk Disclaimer",
  risk_disclaimer: "Signals are for informational purposes only. NOT financial advice. 98% of shitcoins fail. Always use Stop Loss. Never risk more than 1-5% per trade.",
  no_opportunities: "No opportunities right now. The scanner runs every 5 minutes.",
  alerts_title: "Alerts",
  enable_scanner: "Enable Auto-Scanner",
  min_score_label: "Minimum score",
  monitor_timeframes: "Timeframes to monitor",
  max_alerts_day: "Max alerts per day",
  notify_method_label: "Notification method",
  notify_in_app: "In-app only",
  notify_in_app_email: "In-app + email",
  alert_history_title: "Alert History",
  no_alert_history: "No alerts yet.",
  nft_markets: "NFT Marketplaces",
  iran_friendly: "Iran-friendly",
  key_security_note: "Keys are encrypted on the server and never stored in your browser.",
  exchange: "Exchange",
  no_balances: "No balances (add API keys to load live balances).",
  safety: "Safety",
  opportunity: "Opportunity",
  holders: "Holders",
  verdict: "Verdict",
  coin: "Coin",
  chain: "Chain",
  scanning: "Scanning…",
  scanner_subtitle: "Curated memecoins with tradeable liquidity (score ≥ 75). No unknown/scam tokens surfaced.",
  safety_note: "Safety = liquidity lock + mint renounce + holder distribution (indicative, not financial advice).",
  ask_ai: "Ask Firebird AI...",
  send: "Send",
  chat: "Chat",
  ai_empty: "Ask about a coin, timeframe, or strategy. Nothing is financial advice.",
  ai_thinking: "Firebird AI is thinking…",
  ai_error: "Sorry, I couldn't respond right now.",
  score_coin: "Score This Coin",
  any_coin_symbol: "Any coin symbol",
  analyzing: "Analyzing…",
  entry_strategy: "Entry",
  exit_strategy: "Exit",
  risk: "Risk",
  signals_subtitle: "AI + technical scoring across coins",
  momentum: "Momentum",
  simulated_feed: "simulated feed",
  collection: "Firebird 555 Collection",
  nft_subtitle: "Firebird / Phoenix — 555 items · 6 legendary rares · each with its own story of destruction and rebirth",
  rares: "Legendary Rares",
  floor_price: "Floor Price",
  minted: "Minted",
  total_supply: "Total Supply",
  rarity: "Rarity",
  ui_logo_alt: "Firebird / Simorgh Trade logo", ui_logo_switch_hint: "Tap to switch logo", ui_vpn_info: "VPN information",
  bt_download: "Download", bt_stop_loss: "Stop loss (TP = 2× SL)", bt_min_score: "Min signal score",
  bt_share: "Share", bt_history: "Saved backtest runs", bt_card_saved: "Result card downloaded",
  bt_compare_tf: "Compare timeframes", bt_best: "BEST", bt_worst: "WORST",
  bt_run_all: "Run all coins", bt_by_section: "Results by section", bt_per_coin: "Results per coin", bt_avg_return: "Avg return", bt_skipped: "Skipped (not enough data)",
  bt_sec_major: "Major", bt_sec_shitcoin: "Shitcoin", bt_sec_meme: "Meme", bt_sec_nft: "NFT tokens", bt_sec_other: "Other",
  bt_data_live: "LIVE market data", bt_data_simulated: "SIMULATED data (live market unavailable)",
  risk_per_trade: "Risk per trade", max_amount: "Max", tier_major: "Major", tier_mid: "Mid-cap", tier_meme: "Meme/Pump",
  high_risk_ack: "I understand this is a High Risk trade (above 15% of my balance).",
  lev_warn_yellow: "High liquidation risk", lev_warn_red: "Extreme leverage — very high liquidation risk. Double confirmation required.",
  lev_ack: "I understand I can lose my full margin.", lev_confirm_again: "Extreme leverage: press again to confirm.",
  confirm_again: "Confirm again", high_risk_required: "Please tick the High Risk confirmation.", lev_ack_required: "Please confirm the extreme leverage warning.",
  trait_element: "Element", trait_aura: "Aura", trait_background: "Background",
  prev: "Prev",
  next: "Next",
  all: "All",
  save: "Save",
  risk_lock: "Risk Lock",
  max_position: "Max Position %",
  trailing_stop: "Trailing Stop %",
  take_profit: "Take Profit %",
  stop_loss: "Stop Loss %",
  risk_strategy: "Risk & Strategy",
  exchanges: "Exchange Connections",
  connected: "Connected",
  disconnected: "Not Connected",
  withdraw_demo_wallet: "Withdraw (Demo Wallet)",
  withdraw_fee_note: "Withdrawing before 7 days from your last deposit applies a 5% early-withdrawal fee (owner share).",
  withdraw: "Withdraw",
  trade_history: "Trade History",
  no_closed_trades: "No closed trades yet",
  // Owner console
  owner_subtitle: "View-only performance of all users · commission ledger (2% per profitable trade, weekly settlement · 5% early-withdrawal fee)",
  total_users: "Total Users",
  total_commission: "Total Commission",
  this_week: "This Week",
  total_volume: "Total Volume",
  all_time: "all-time",
  settles_weekly: "settles weekly",
  commission_breakdown: "Commission Breakdown",
  from_trade_profits: "From trade profits (2%)",
  from_withdrawal_fees: "From early-withdrawal fees (5%)",
  owner_rate: "Owner rate",
  settlement: "Settlement",
  early_exit_fee: "Early-exit fee",
  weekly: "Weekly",
  daily_accrual: "Daily Accrual",
  no_commission_yet: "No commission yet — accrues on profitable trades.",
  users_performance: "Users Performance (view-only)",
  col_user: "User",
  col_role: "Role",
  col_joined: "Joined",
  col_volume: "Volume",
  col_balance: "Balance",
  role_owner: "owner",
  role_user: "user",
  // Guide steps
  guide_intro: "Welcome to Firebird AI Trade. Follow these steps to get started safely.",
  guide_step: "Step",
  guide1_title: "Demo Mode is on",
  guide1_desc: "You start with a $100 demo balance. No real money is used until you explicitly enable live trading with your own secure keys.",
  guide2_title: "Read the Dashboard",
  guide2_desc: "See portfolio value, top AI signals, market overview, whale activity, and market pulse in one place.",
  guide3_title: "Check AI Signals",
  guide3_desc: "Coins are scored 0-100 using RSI, SMA crossovers and momentum across timeframes.",
  guide4_title: "Ask the AI Assistant",
  guide4_desc: "Get a coin score with entry/exit strategy and risk notes from Firebird AI Core (GPT-5.6).",
  guide5_title: "Try Auto-Trade (Demo)",
  guide5_desc: "Let the bot open BUY positions on strong signals and manage them with trailing stop / take-profit.",
  guide6_title: "Scan Memecoins",
  guide6_desc: "Only tradeable memecoins with high safety scores are shown — no unknown or scam tokens.",
  guide7_title: "Explore Firebird 555 NFTs",
  guide7_desc: "Browse the 555-item phoenix collection with 6 legendary rares.",
  disclaimer:
    "Demo/sandbox mode. Nothing here is financial advice. No system can guarantee future win rates.",
  // Dynamic values
  src_manual: "Manual",
  src_auto: "Auto",
  tier_legendary: "Legendary",
  tier_epic: "Epic",
  tier_rare: "Rare",
  tier_common: "Common",
  verdict_tradeable: "Tradeable",
  verdict_watch: "Watch",
  pressure_accumulation: "Accumulation",
  pressure_distribution: "Distribution",
  reason_take_profit: "Take Profit",
  reason_liquidation: "Liquidation",
  reason_stop_loss: "Stop Loss",
  reason_trailing_take_profit: "Trailing Take Profit",
  reason_trailing_stop: "Trailing Stop",
  reason_signal_flip: "Signal Flip",
  // Header / layout
  welcome: "Welcome",
  trader: "trader",
  live: "Live",
  // Manual trade
  verify_email_banner: "Verify your email to open trades. Check your inbox for the verification link.",
  resend: "Resend",
  verification_sent: "Verification email sent",
  could_not_resend: "Could not resend",
  search_coin_placeholder: "Search any coin (e.g. BTC, PEPE, ARB)",
  add_to_watchlist: "Add to watchlist",
  free_balance: "Free Balance",
  in_positions: "In Positions",
  total_equity: "Total Equity",
  watchlist: "Watchlist",
  watchlist_empty: "No coins yet — search and tap + to add.",
  high_exposure: "Using {percent}% of your free balance — high exposure.",
  advanced_tpsl: "Advanced TP / SL",
  entry_optional: "Entry (optional — defaults to market)",
  market: "market",
  take_profit_price: "Take Profit price",
  stop_loss_price: "Stop Loss price",
  tp_example: "e.g. 72000",
  sl_example: "e.g. 66000",
  if_tp_hits: "If TP hits",
  if_sl_hits: "If SL hits",
  pnl_preview_note: "Based on ${amount} margin × {leverage}x. Lines drawn on chart →",
  clear_tpsl: "Clear TP/SL",
  one_tap_trade: "One-Tap Trade",
  tp_short: "TP",
  sl_short: "SL",
  auto_signals: "Auto Signals",
  add_token_placeholder: "Add any token (e.g., ARB, OP, TIA)",
  analyze: "Analyze",
  analyzing_on: "Analyzing {symbol} on {tf}…",
  tap_to_load: "Tap to load on chart →",
  scanning_market: "Scanning the market…",
  no_signals: "No signals yet.",
  money_depth: "Money Depth",
  sr_zones: "S/R Zones",
  bot_analysis: "Bot Analysis",
  confidence: "Confidence",
  reasoning: "Reasoning",
  tp_label: "Take Profit",
  sl_label: "Stop Loss",
  risk_reward: "Risk : Reward",
  levels_from_position: "Levels live from your open position",
  levels_suggested: "Suggested levels · not financial advice",
  toast_opened: "Opened {symbol} {leverage}x position",
  toast_closed: "Closed {symbol} · PnL ${pnl}",
  toast_closed_pnl: "Closed · PnL ${pnl}",
  ago_s: "{n}s ago",
  ago_m: "{n} min ago",
  ago_h: "{n}h ago",
  ago_d: "{n}d ago",
  in_label: "In",
  out_label: "Out",
  // Owner
  confirm_reveal_email: "Reveal this user's email? Emails are private — only view when necessary.",
  view_email_private: "View email (private)",
  // Settings / security
  display_name_updated: "Display name updated",
  change_email_sent: "Verification link sent to the new email",
  username_updated: "Username updated",
  password_changed: "Password changed. Sign in again on your other devices.",
  confirm_logout_all: "Sign out from all devices? You'll need to log in again.",
  no_wallet: "No wallet extension detected (MetaMask/WalletConnect).",
  wallet_rejected: "Wallet connection rejected.",
  profile_security: "Profile & Security",
  display_name: "Display Name",
  display_name_optional: "Display Name (optional)",
  edit: "Edit",
  cancel: "Cancel",
  change: "Change",
  in_days: "In {days}d",
  username_label: "Username",
  username_hint: "3-20 letters, numbers or underscore. Changeable once every 30 days.",
  username_example: "e.g. arash_king",
  email_private: "Email (private)",
  change_email: "Change Email",
  unverified: "Unverified",
  resend_verification: "Resend verification",
  new_email: "New email",
  current_password: "Current password",
  new_password: "New password",
  confirm_new_password: "Confirm new password",
  send_verification_new: "Send verification to new email",
  change_email_hint: "You'll confirm via a link sent to the new email, then sign in again.",
  email_private_hint: "Your email is never shown publicly. Only you can see it here.",
  sessions: "Sessions",
  logout_all_devices: "Logout from all devices",
  change_password: "Change Password",
  pw_hint: "Min 8 chars, a letter and a number.",
  update_password: "Update Password",
  // Assistant
  coin_example_placeholder: "e.g. BTC, PEPE, TAO...",
  // Login
  username_or_email: "Username or Email",
  forgot_password_q: "Forgot Password?",
  // Register
  invalid_email: "Enter a valid email",
  pw_mismatch: "Passwords do not match",
  username_hint_short: "3-20 letters, numbers or underscore",
  account_created_verify: "Account created! Check your email to verify your account.",
  confirm_password_label: "Confirm Password",
  // Verify
  missing_verify_token: "Missing verification token.",
  email_verified_msg: "Email verified!",
  verification_failed: "Verification failed.",
  verifying_email: "Verifying your email…",
  email_verified_title: "Email Verified",
  go_to_login: "Go to Login",
  verification_failed_title: "Verification Failed",
  verify_expired_hint: "Link expired? Sign in and use \"Resend verification\" in Settings.",
  back_to_login: "← Back to login",
  // Forgot password
  forgot_password: "Forgot Password",
  forgot_subtitle: "Enter your username or email and we'll send a reset link.",
  forgot_sent_toast: "If the account exists, a reset link has been sent.",
  forgot_sent_full: "If the account exists, a reset link has been sent. Check your email (valid 15 minutes).",
  send_reset_link: "Send reset link",
  remembered_it: "Remembered it?",
  // Reset password
  missing_reset_token: "Missing reset token. Use the link from your email.",
  pw_requirement: "Password must be at least 8 characters and include a letter and a number",
  password_updated: "Password updated.",
  reset_password: "Reset Password",
  reset_subtitle: "Choose a new password for your account.",
  reset_invalid: "Invalid or missing reset link. Request a new one.",
  request_reset_link: "Request reset link",
  new_password_label: "New Password",
  pw_hint2: "At least 8 characters, including a letter and a number.",
  update_password_btn: "Update password",
  // Dynamic (tv)
  bullish: "Bullish",
  bearish: "Bearish",
  inflow: "inflow",
  outflow: "outflow",
  // Chart (TradingChart)
  volume_up: "Volume up",
  volume_down: "Volume down",
  money_flow_mfi: "Money Flow (MFI 14)",
  flow_overbought: "Overbought",
  flow_oversold: "Oversold",
  flow_bullish: "Bullish inflow",
  flow_bearish: "Bearish outflow",
  support: "Support",
  resistance: "Resistance",
  // Signals panel v2 (tabs, filters, confirm modal, NFT trading)
  available_for_trade: "Available for trade",
  free_balance_after: "Free Balance after",
  tab_major: "Major",
  tab_shitcoins: "Shitcoins",
  tab_nfts: "NFTs",
  filter_direction: "Direction",
  buy_only: "Buy only",
  sell_only: "Sell only",
  min_confidence: "Min Confidence",
  confirm: "Confirm",
  safety_score: "Safety Score",
  liquidity_lock: "Liquidity Lock",
  locked: "Locked",
  unlocked: "Unlocked",
  honeypot: "Honeypot",
  honeypot_safe_label: "Safe",
  honeypot_risk: "Risk",
  nft_demo_notice: "Demo trade — no real NFT will be purchased",
  view_on_opensea: "View on OpenSea",
  volume_24h: "Volume (24h)",
  nft_position_opened: "NFT position opened (Demo)",
  crypto_positions_title: "Crypto & Shitcoin Positions",
  nft_positions_title: "NFT Positions",
  min_safety_required: "Safety Score must be ≥ 75 to trade",
  amount_exceeds_balance: "Amount exceeds your free balance",
  amount_gt_zero: "Amount must be greater than 0",
  confirm_trade: "Confirm Trade",
  usd_equiv: "≈",
  nft_badge: "NFT",
  no_nft_positions: "No open NFT positions",
  floor: "Floor",
  rr_short: "R:R",
  best_marketplace: "Best Market",
  story: "Story",
  show_more: "Show more",
  show_less: "Show less",
  view_chart: "View Chart",
  // Signal reason fragments
  reason_sma_bull: "SMA9 above SMA21 (bullish trend)",
  reason_sma_bear: "SMA9 below SMA21 (bearish trend)",
  reason_rsi_oversold: "oversold",
  reason_rsi_overbought: "overbought",
  reason_rsi_neutral: "neutral",
  reason_pos_momentum: "Positive momentum",
  reason_neg_momentum: "Negative momentum",
  reason_insufficient: "Insufficient data",
  reason_whale_accum: "Whale accumulation",
  reason_rising_floor: "Rising floor",
  reason_falling_floor: "Falling floor",
  reason_high_rarity: "High rarity",
  reason_mid_rarity: "Mid rarity",
  // NFT trade history
  nft_trade_history: "NFT Trade History",
  no_nft_trades: "No closed NFT trades yet",
  entry_floor: "Entry Floor",
  exit_floor: "Exit Floor",
  // Verify reminder banner
  verify_reminder: "Your email isn't verified yet — verify to unlock trading.",
  verify_now: "Verify now",
  dismiss: "Dismiss",
  // Trusted devices
  trusted_devices: "Active Sessions · Logged-in Devices",
  font_size: "Font Size", font_small: "Small", font_medium: "Medium", font_large: "Large",
  sessions_hint: "Phones and browsers currently signed in to your account (not exchange API keys).", unknown_device: "Unknown device",
  this_device: "This device",
  current_session: "Current",
  last_active: "Last active",
  revoke: "Revoke",
  revoke_all_others: "Sign out all other devices",
  revoke_all_others_done: "Signed out from all other devices",
  no_other_devices: "No other active devices",
  device_revoked: "Device signed out",
};

const fa = {
  app_name: "فایربرد AI",
  tagline: "ترید هوشمند کریپتو و NFT",
  login: "ورود",
  register: "ثبت‌نام",
  logout: "خروج",
  email: "ایمیل",
  password: "رمز عبور",
  name: "نام",
  welcome_back: "خوش آمدی، تریدر",
  create_account: "ساخت حساب کاربری",
  no_account: "هنوز حساب نداری؟",
  have_account: "قبلاً حساب ساخته‌ای؟",
  account_created: "حساب ساخته شد — دمو 100 دلاری آماده است",
  loading: "در حال بارگذاری…",
  demo_mode: "حالت دمو",
  demo_balance: "موجودی دمو",
  nav_dashboard: "داشبورد",
  nav_charts: "چارت زنده",
  nav_signals: "سیگنال‌های AI",
  nav_autotrade: "ترید خودکار",
  nav_manual: "ترید دستی",
  nav_scanner: "اسکنر میم‌کوین",
  nav_backtest: "بک‌تست 5×5",
  nav_positions: "پوزیشن‌ها و گزارش‌ها",
  nav_nft: "کالکشن NFT",
  nav_assistant: "دستیار هوش مصنوعی",
  nav_exchange: "صرافی",
  nav_settings: "تنظیمات",
  nav_subscription: "اشتراک ویژه",
  subscription_subtitle: "هوش معاملاتی ویژه را فعال کنید — پرداخت با ارز دیجیتال.",
  current_plan: "پلن فعلی",
  premium: "ویژه",
  free_plan: "پلن رایگان",
  renews_on: "تمدید در",
  active: "فعال",
  best_value: "بهترین ارزش",
  plan_monthly: "ماهانه",
  plan_annual: "سالانه",
  plan_test: "تست",
  days: "روز",
  pay_with_crypto: "پرداخت با ارز دیجیتال",
  opening_checkout: "در حال باز کردن درگاه…",
  gateway_not_ready: "درگاه پرداخت ارز دیجیتال هنوز پیکربندی نشده است. پس از افزودن کلیدهای NOWPayments، پرداخت اشتراک ویژه فعال می‌شود.",
  premium_only: "ویژه اعضای پرمیوم",
  premium_gate_desc: "این بخش بخشی از اشتراک ویژه فایربرد است. برای دسترسی کامل به سیگنال‌های هوش مصنوعی، معامله خودکار و اسکنر فرصت‌ها ارتقا دهید.",
  go_premium: "ارتقا به ویژه",
  billing_history: "تاریخچه پرداخت‌ها",
  no_invoices: "هنوز فاکتوری ثبت نشده است. پرداخت‌های ارز دیجیتال شما اینجا نمایش داده می‌شود.",
  date: "تاریخ",
  plan_col: "پلن",
  amount_col: "مبلغ",
  status_col: "وضعیت",
  view_invoice: "مشاهده فاکتور",
  check_status: "بررسی وضعیت پرداخت",
  checking: "در حال بررسی…",
  payment_still_pending: "پرداخت هنوز تأیید نشده است. به‌صورت خودکار بررسی را ادامه می‌دهیم.",
  status_paid: "پرداخت شده",
  status_created: "در انتظار",
  status_partially_paid: "پرداخت ناقص",
  status_expired: "منقضی شده",
  status_failed: "ناموفق",
  status_refunded: "بازگشت داده شده",
  status_cancelled: "لغو شده",
  status_canceled: "لغو شده",
  payment_processing: "پرداخت شناسایی شد — در حال فعال‌سازی پلن ویژه…",
  payment_success: "پلن ویژه فعال شد. خوش آمدید!",
  payment_cancelled: "پرداخت لغو شد.",
  payment_error: "شروع پرداخت ممکن نشد. لطفاً دوباره تلاش کنید.",
  popup_blocked: "درگاه پرداخت آماده است. اگر تب جدیدی باز نشد، از لینک زیر استفاده کنید.",
  checkout_opened_hint: "درگاه پرداخت در تب جدید باز شد. آن را ندیدید؟",
  open_checkout: "باز کردن درگاه پرداخت",
  pay_currency_label: "پرداخت با",
  pay_currency_hint: "ما USDC در شبکه سولانا و BNB در شبکه BSC را می‌پذیریم. دارایی انتخابی شما هنگام پرداخت قفل می‌شود.",
  feat_signals: "سیگنال‌های کامل هوش مصنوعی (کریپتو، شیتکوین، NFT)",
  feat_autotrade: "معامله خودکار با مدیریت ریسک",
  feat_scanner: "اسکنر میم‌کوین و فرصت‌ها",
  feat_alerts: "هشدارهای هوشمند ایمیلی",
  feat_priority: "ارسال سیگنال با اولویت",
  feat_save: "2 ماه رایگان نسبت به ماهانه",
  feat_test_quick: "تست یک‌باره 1 دلاری — برای بررسی جریان پرداخت واقعی",
  demo_account_settings: "تنظیمات حساب دمو",
  demo_settings_desc: "یک موجودی دمو دلخواه تنظیم کنید و حساب معاملات آزمایشی خود را بازنشانی کنید. این فقط روی داده‌های دمو اثر می‌گذارد و هرگز به اشتراک یا معاملات واقعی شما دست نمی‌زند.",
  custom_demo_balance: "موجودی دمو دلخواه (USDC)",
  demo_min_hint: "پیش‌فرض 500 · حداقل 100",
  reset_demo_account: "بازنشانی حساب دمو",
  reset_demo_confirm_title: "حساب دمو بازنشانی شود؟",
  reset_demo_confirm_desc: "این کار موجودی دمو را روی ${bal} دلار تنظیم می‌کند، همه معاملات دمو و تاریخچه سود/زیان را حذف می‌کند و وضعیت افت سرمایه، توقف و دوره خنک‌شدن را پاک می‌کند. این عمل قابل بازگشت نیست. اشتراک و داده‌های واقعی شما تحت تأثیر قرار نمی‌گیرد.",
  demo_reset_done: "حساب دمو بازنشانی شد — موجودی روی ${bal} دلار تنظیم شد",
  demo_min_balance_error: "حداقل موجودی دمو 100 است",
  nav_guide: "راهنمای تازه‌واردها",
  owner_console: "کنسول مالک",
  total_value: "ارزش کل",
  cash: "نقد",
  holdings: "دارایی‌ها",
  unrealized_pnl: "سود/زیان باز",
  top_signals: "برترین سیگنال‌های AI",
  market_overview: "نمای بازار",
  whale_activity: "نهنگ‌ها و پول هوشمند",
  market_pulse: "نبض بازار",
  news_up: "{name} در 24 ساعت {pct}% رشد کرد", news_down: "{name} در 24 ساعت {pct}% افت کرد",
  kind_cex: "صرافی متمرکز", kind_iranian_cex: "صرافی ایرانی", kind_dex: "صرافی غیرمتمرکز",
  price: "قیمت",
  change_24h: "24 ساعت",
  score: "امتیاز",
  action: "اقدام",
  buy: "خرید",
  sell: "فروش",
  hold: "نگه‌داری",
  timeframe: "تایم‌فریم",
  symbol: "نماد",
  amount_usd: "مبلغ (دلار)",
  open_position: "باز کردن پوزیشن",
  close: "بستن",
  no_positions: "پوزیشن بازی وجود ندارد",
  entry: "ورود",
  current: "فعلی",
  exit: "خروج",
  reason: "دلیل",
  pnl: "سود/زیان",
  win_rate: "نرخ برد",
  total_trades: "کل معاملات",
  total_pnl: "سود/زیان کل",
  wl: "برد / باخت",
  open_label: "باز",
  run_backtest: "اجرای بک‌تست",
  running: "در حال اجرا…",
  bt_subtitle: "استراتژی روی 5 بازه تاریخی در هر تایم‌فریم آزموده شد.",
  window_results: "نتایج بازه‌ها",
  window: "بازه",
  trades: "معاملات",
  return_label: "بازده",
  max_drawdown: "حداکثر افت",
  auto_trade_on: "ترید خودکار فعال",
  auto_trade_off: "ترید خودکار خاموش",
  enable_autotrade: "فعال‌سازی ترید خودکار (دمو)",
  reset_demo: "ریست دمو ($100)",
  activity_log: "گزارش فعالیت",
  waiting_signals: "در انتظار سیگنال…",
  enable_to_start: "برای شروع، ترید خودکار را فعال کن",
  reauthorize_bot: "فعال‌سازی مجدد ربات",
  leverage: "اهرم",
  high_leverage_warning: "اهرم بالا — ممکن است کل مارجین خود را از دست بدهید (لیکویید).",
  bot_description: "ربات همه کوین‌ها را در تایم‌فریم‌های 15د / 1س / 4س اسکن می‌کند، بهترین سیگنال را وارد می‌شود (نسبت سود به ریسک 2:1 با حد ضرر الزامی) و پوزیشن‌ها را با حد سود شناور در حالت دمو مدیریت می‌کند.",
  rule_rr: "نسبت سود به ریسک 2:1 (حد ضرر الزامی)",
  rule_max_concurrent: "حداکثر 2 ترید خودکار هم‌زمان",
  rule_leverage: "اهرم خودکار ≤ 10x · دستی ≤ 50x",
  rule_circuit: "قطع‌کننده مدار در افت 20%",
  rule_halt: "توقف پس از 5 باخت",
  rule_scan: "اسکن همه کوین‌ها و تایم‌فریم‌ها",
  rule_entry: "ورود با امتیاز ≥ {score} در {tfs}",
  rule_cooldown: "خنک‌سازی {min} دقیقه پس از {losses} ضرر",
  risk_status: "وضعیت ریسک",
  risk_mode_active: "فعال",
  risk_mode_halted: "متوقف",
  consecutive_losses_label: "باخت‌های متوالی",
  drawdown_label: "افت سرمایه",
  open_positions_label: "پوزیشن‌های باز",
  max_concurrent_reached: "به حداکثر معاملات هم‌زمان رسیدید.",
  bot_reauthorized: "ربات دوباره مجاز شد. می‌توانید ترید خودکار را دوباره فعال کنید.",
  cloud_trading: "ترید ابری",
  enable_cloud: "فعال‌سازی ترید ابری",
  disable_cloud: "غیرفعال‌سازی ترید ابری",
  cloud_status: "وضعیت ابری",
  cloud_on: "در حال اجرا 24/7",
  cloud_off: "خاموش",
  cloud_desc: "ربات روی سرورهای ما به‌صورت 24/7 اجرا می‌شود، حتی وقتی دستگاه شما خاموش است.",
  notifications: "اعلان‌ها",
  no_notifications: "هنوز اعلانی نیست",
  mark_all_read: "علامت‌گذاری همه به‌عنوان خوانده‌شده",
  connect_exchange: "اتصال صرافی",
  api_key: "کلید API",
  api_secret: "رمز API",
  api_key_optional: "کلید API (اختیاری برای قیمت عمومی)",
  connect: "اتصال",
  test: "تست",
  remove: "حذف",
  verified: "تأییدشده",
  not_verified: "تأیید نشده",
  connected_exchanges: "صرافی‌های متصل",
  aggregated_balances: "موجودی · همه صرافی‌ها",
  aggregated_prices: "قیمت زنده · همه صرافی‌ها",
  no_connections: "هنوز صرافی متصل نشده است.",
  connect_wallet: "اتصال کیف پول",
  wallet_connected: "کیف پول متصل شد",
  wallet_note: "غیرحضانتی — ما هرگز کلید خصوصی شما را ذخیره نمی‌کنیم.",
  wallet_security: "کیف پول و امنیت",
  wallet_never_ask: "ما هرگز کلید خصوصی یا عبارت بازیابی شما را نمی‌خواهیم.",
  disconnect_wallet: "قطع اتصال کیف پول",
  switch_chain: "تغییر شبکه",
  native_balance: "موجودی اصلی",
  wallet_chain: "شبکه",
  dex_onchain: "دکس (روی زنجیره)",
  sign_in_wallet: "امضا در کیف پول",
  waiting_signature: "در انتظار امضای کیف پول…",
  dex_demo_note: "شما در کیف پول خودتان امضا می‌کنید — کلیدها هرگز از دستگاه شما خارج نمی‌شوند.",
  view_on_explorer: "مشاهده در اکسپلورر",
  wallet_required_dex: "برای معامله روی زنجیره، کیف پول خود را متصل کنید.",
  tx_pending: "تراکنش در حال انجام…",
  tx_confirmed: "تراکنش تأیید شد",
  tx_failed: "تراکنش ناموفق بود",
  connected_label: "متصل",
  not_connected_label: "متصل نیست",
  copy_address: "کپی آدرس",
  readonly_warning: "کلید API را فقط با دسترسی «فقط‌خواندنی» (READ-ONLY) بسازید. برداشت (Withdraw) و معامله (Trade) را غیرفعال کنید.",
  cex_security_notice: "ما هرگز کلید خصوصی شما را ذخیره نمی‌کنیم و هرگز دسترسی برداشت (Withdraw) نمی‌خواهیم.",
  api_passphrase_optional: "عبارت عبور (اختیاری)",
  read_only_label: "فقط‌خواندنی",
  how_to_create_key: "راهنمای ساخت کلید فقط‌خواندنی",
  connected_wallets: "کیف‌پول‌های متصل",
  no_wallet_connected: "هیچ کیف پولی متصل نیست.",
  no_exchange_linked: "هیچ صرافی‌ای متصل نیست.",
  smart_contract_allowances: "مجوزهای قرارداد هوشمند",
  no_allowances: "هیچ مجوز فعالی وجود ندارد.",
  revoke: "لغو",
  max_spend: "سقف مجاز",
  link_readonly_hint: "برای افزودن کلید فقط‌خواندنی از پنل «Connect Exchange» استفاده کنید.",
  vpn_notice: "این مارکت‌پلیس ممکن است برای کاربران برخی مناطق نیاز به VPN داشته باشد.",
  ir_friendly: "مناسب ایران",
  best_price: "بهترین قیمت",
  marketplace_listings: "لیست مارکت‌پلیس‌ها",
  tab_opps: "فرصت‌ها",
  opportunities: "فرصت‌ها",
  high_opportunity: "فرصت بالا",
  alerts_triggered_today: "هشدار امروز فعال شد",
  score_range: "بازه امتیاز",
  timeframe_label: "تایم‌فریم",
  direction_label: "جهت",
  risk_disclaimer_title: "سلب مسئولیت ریسک",
  risk_disclaimer: "سیگنال‌ها فقط برای اطلاع‌رسانی هستند. توصیه مالی نیستند. 98% میم‌کوین‌ها شکست می‌خورند. همیشه از حد ضرر استفاده کنید و بیش از 1-5% سرمایه در هر معامله ریسک نکنید.",
  no_opportunities: "در حال حاضر فرصتی نیست. اسکنر هر 5 دقیقه اجرا می‌شود.",
  alerts_title: "هشدارها",
  enable_scanner: "فعال‌سازی اسکنر خودکار",
  min_score_label: "حداقل امتیاز",
  monitor_timeframes: "تایم‌فریم‌های تحت نظر",
  max_alerts_day: "حداکثر هشدار در روز",
  notify_method_label: "روش اطلاع‌رسانی",
  notify_in_app: "فقط درون‌برنامه",
  notify_in_app_email: "درون‌برنامه + ایمیل",
  alert_history_title: "تاریخچه هشدارها",
  no_alert_history: "هنوز هشداری نیست.",
  nft_markets: "بازارهای NFT",
  iran_friendly: "مناسب ایران",
  key_security_note: "کلیدها روی سرور رمزنگاری می‌شوند و هرگز در مرورگر شما ذخیره نمی‌شوند.",
  exchange: "صرافی",
  no_balances: "موجودی‌ای نیست (برای بارگذاری موجودی زنده، کلید API اضافه کنید).",
  safety: "امنیت",
  opportunity: "فرصت",
  holders: "هولدرها",
  verdict: "نتیجه",
  coin: "کوین",
  chain: "زنجیره",
  scanning: "در حال اسکن…",
  scanner_subtitle: "میم‌کوین‌های منتخب با نقدینگی قابل معامله (امتیاز ≥ 75). هیچ توکن ناشناس/کلاهبرداری نمایش داده نمی‌شود.",
  safety_note: "امنیت = قفل نقدینگی + واگذاری مینت + توزیع هولدرها (اطلاعاتی، نه توصیه مالی).",
  ask_ai: "از فایربرد AI بپرس...",
  send: "ارسال",
  chat: "گفتگو",
  ai_empty: "درباره یک کوین، تایم‌فریم یا استراتژی بپرس. هیچ‌چیز توصیه مالی نیست.",
  ai_thinking: "فایربرد AI در حال فکر کردن است…",
  ai_error: "متأسفم، الان نتوانستم پاسخ دهم.",
  score_coin: "امتیازدهی این کوین",
  any_coin_symbol: "هر نماد کوین",
  analyzing: "در حال تحلیل…",
  entry_strategy: "ورود",
  exit_strategy: "خروج",
  risk: "ریسک",
  signals_subtitle: "امتیازدهی هوش مصنوعی + تکنیکال روی کوین‌ها",
  momentum: "مومنتوم",
  simulated_feed: "فید شبیه‌سازی‌شده",
  collection: "کالکشن فایربرد 555",
  nft_subtitle: "فایربرد / ققنوس — 555 آیتم · 6 نایاب افسانه‌ای · هرکدام با داستان نابودی و تولد دوباره خود",
  rares: "نایاب‌های افسانه‌ای",
  floor_price: "کف قیمت",
  minted: "مینت‌شده",
  total_supply: "کل عرضه",
  rarity: "نادر بودن",
  ui_logo_alt: "لوگوی Firebird / Simorgh Trade", ui_logo_switch_hint: "برای تغییر لوگو بزنید", ui_vpn_info: "اطلاعات VPN",
  bt_download: "دانلود", bt_stop_loss: "حد ضرر (حد سود = 2 برابر)", bt_min_score: "حداقل امتیاز سیگنال",
  bt_share: "اشتراک‌گذاری", bt_history: "بک‌تست‌های ذخیره‌شده", bt_card_saved: "کارت نتیجه دانلود شد",
  bt_compare_tf: "مقایسه تایم‌فریم‌ها", bt_best: "بهترین", bt_worst: "بدترین",
  bt_run_all: "اجرا روی همه کوین‌ها", bt_by_section: "نتایج بر اساس بخش", bt_per_coin: "نتایج هر کوین", bt_avg_return: "میانگین بازده", bt_skipped: "رد شد (داده کافی نیست)",
  bt_sec_major: "اصلی", bt_sec_shitcoin: "شیت‌کوین", bt_sec_meme: "میم", bt_sec_nft: "توکن‌های NFT", bt_sec_other: "سایر",
  bt_data_live: "داده واقعی بازار", bt_data_simulated: "داده شبیه‌سازی‌شده (بازار زنده در دسترس نیست)",
  risk_per_trade: "ریسک هر معامله", max_amount: "حداکثر", tier_major: "اصلی", tier_mid: "میان‌رده", tier_meme: "میم/پامپ",
  high_risk_ack: "می‌دانم این یک معامله پرریسک است (بیش از 15% موجودی من).",
  lev_warn_yellow: "ریسک بالای لیکویید شدن", lev_warn_red: "اهرم بسیار بالا — ریسک شدید لیکویید شدن. تأیید دوباره لازم است.",
  lev_ack: "می‌دانم ممکن است کل مارجین خود را از دست بدهم.", lev_confirm_again: "اهرم بسیار بالا: برای تأیید دوباره بزنید.",
  confirm_again: "تأیید دوباره", high_risk_required: "لطفاً تأیید معامله پرریسک را علامت بزنید.", lev_ack_required: "لطفاً هشدار اهرم بسیار بالا را تأیید کنید.",
  trait_element: "عنصر", trait_aura: "هاله", trait_background: "پس‌زمینه",
  prev: "قبلی",
  next: "بعدی",
  all: "همه",
  save: "ذخیره",
  risk_lock: "قفل ریسک",
  max_position: "حداکثر پوزیشن %",
  trailing_stop: "حد ضرر شناور %",
  take_profit: "حد سود %",
  stop_loss: "حد ضرر %",
  risk_strategy: "ریسک و استراتژی",
  exchanges: "اتصال صرافی‌ها",
  connected: "متصل",
  disconnected: "متصل نیست",
  withdraw_demo_wallet: "برداشت (کیف پول دمو)",
  withdraw_fee_note: "برداشت زودتر از 7 روز پس از آخرین واریز، کارمزد برداشت زودهنگام 5% دارد (سهم مالک).",
  withdraw: "برداشت",
  trade_history: "تاریخچه معاملات",
  no_closed_trades: "هنوز معامله بسته‌شده‌ای نیست",
  owner_subtitle: "عملکرد همه کاربران (فقط مشاهده) · دفتر کمیسیون (2% هر معامله سودده، تسویه هفتگی · کارمزد برداشت زودهنگام 5%)",
  total_users: "کل کاربران",
  total_commission: "کل کمیسیون",
  this_week: "این هفته",
  total_volume: "حجم کل",
  all_time: "از ابتدا",
  settles_weekly: "تسویه هفتگی",
  commission_breakdown: "تفکیک کمیسیون",
  from_trade_profits: "از سود معاملات (2%)",
  from_withdrawal_fees: "از کارمزد برداشت زودهنگام (5%)",
  owner_rate: "نرخ مالک",
  settlement: "تسویه",
  early_exit_fee: "کارمزد خروج زودهنگام",
  weekly: "هفتگی",
  daily_accrual: "انباشت روزانه",
  no_commission_yet: "هنوز کمیسیونی نیست — با معاملات سودده انباشته می‌شود.",
  users_performance: "عملکرد کاربران (فقط مشاهده)",
  col_user: "کاربر",
  col_role: "نقش",
  col_joined: "عضویت",
  col_volume: "حجم",
  col_balance: "موجودی",
  role_owner: "مالک",
  role_user: "کاربر",
  guide_intro: "به فایربرد AI ترید خوش آمدی. برای شروع امن این مراحل را دنبال کن.",
  guide_step: "مرحله",
  guide1_title: "حالت دمو فعال است",
  guide1_desc: "با موجودی دمو 100 دلاری شروع می‌کنی. تا زمانی که خودت ترید واقعی را با کلیدهای امن خودت فعال نکنی، هیچ پول واقعی استفاده نمی‌شود.",
  guide2_title: "داشبورد را بخوان",
  guide2_desc: "ارزش پرتفوی، برترین سیگنال‌های AI، نمای بازار، فعالیت نهنگ‌ها و نبض بازار را یکجا ببین.",
  guide3_title: "سیگنال‌های AI را بررسی کن",
  guide3_desc: "کوین‌ها با RSI، تقاطع میانگین‌ها و مومنتوم در تایم‌فریم‌ها، از 0 تا 100 امتیاز می‌گیرند.",
  guide4_title: "از دستیار AI بپرس",
  guide4_desc: "امتیاز کوین به همراه استراتژی ورود/خروج و نکات ریسک را از هسته فایربرد AI (GPT-5.6) بگیر.",
  guide5_title: "ترید خودکار را امتحان کن (دمو)",
  guide5_desc: "بگذار ربات روی سیگنال‌های قوی پوزیشن خرید باز کند و با حد ضرر شناور / حد سود مدیریت‌شان کند.",
  guide6_title: "میم‌کوین‌ها را اسکن کن",
  guide6_desc: "فقط میم‌کوین‌های قابل معامله با امتیاز امنیت بالا نمایش داده می‌شوند — بدون توکن ناشناس یا کلاهبرداری.",
  guide7_title: "NFTهای فایربرد 555 را کشف کن",
  guide7_desc: "کالکشن 555 تایی ققنوس با 6 نایاب افسانه‌ای را مرور کن.",
  disclaimer:
    "حالت دمو/سندباکس. هیچ‌چیز اینجا توصیه مالی نیست. هیچ سیستمی نرخ برد آینده را تضمین نمی‌کند.",
  src_manual: "دستی",
  src_auto: "خودکار",
  tier_legendary: "افسانه‌ای",
  tier_epic: "حماسی",
  tier_rare: "نایاب",
  tier_common: "معمولی",
  verdict_tradeable: "قابل معامله",
  verdict_watch: "زیر نظر",
  pressure_accumulation: "انباشت",
  pressure_distribution: "توزیع",
  reason_take_profit: "حد سود",
  reason_liquidation: "لیکویید",
  reason_stop_loss: "حد ضرر",
  reason_trailing_take_profit: "حد سود شناور",
  reason_trailing_stop: "حد ضرر شناور",
  reason_signal_flip: "چرخش سیگنال",
  welcome: "خوش آمدی",
  trader: "تریدر",
  live: "زنده",
  verify_email_banner: "برای باز کردن معامله، ایمیل خود را تأیید کنید. برای لینک تأیید، صندوق ورودی ایمیل خود را بررسی کنید.",
  resend: "ارسال مجدد",
  verification_sent: "ایمیل تأیید ارسال شد",
  could_not_resend: "ارسال مجدد ناموفق بود",
  search_coin_placeholder: "جستجوی هر ارز (مثلاً BTC، PEPE، ARB)",
  add_to_watchlist: "افزودن به لیست پیگیری",
  free_balance: "موجودی آزاد",
  in_positions: "در پوزیشن‌ها",
  total_equity: "ارزش کل",
  watchlist: "لیست پیگیری",
  watchlist_empty: "هنوز ارزی نیست — جستجو کن و با + اضافه کن.",
  high_exposure: "استفاده از {percent}% موجودی آزاد — ریسک بالا.",
  advanced_tpsl: "حد سود / حد ضرر پیشرفته",
  entry_optional: "ورود (اختیاری — پیش‌فرض قیمت بازار)",
  market: "بازار",
  take_profit_price: "قیمت حد سود",
  stop_loss_price: "قیمت حد ضرر",
  tp_example: "مثلاً 72000",
  sl_example: "مثلاً 66000",
  if_tp_hits: "اگر حد سود فعال شود",
  if_sl_hits: "اگر حد ضرر فعال شود",
  pnl_preview_note: "بر اساس {amount} دلار مارجین × {leverage}x. خطوط روی چارت رسم شده ←",
  clear_tpsl: "پاک کردن حد سود/ضرر",
  one_tap_trade: "معامله تک‌لمسی",
  tp_short: "حد سود",
  sl_short: "حد ضرر",
  auto_signals: "سیگنال‌های خودکار",
  add_token_placeholder: "افزودن هر توکن (مثلاً ARB، OP، TIA)",
  analyze: "تحلیل",
  analyzing_on: "در حال تحلیل {symbol} در {tf}…",
  tap_to_load: "برای بارگذاری روی چارت بزنید ←",
  scanning_market: "در حال اسکن بازار…",
  no_signals: "هنوز سیگنالی نیست.",
  money_depth: "عمق پول",
  sr_zones: "نواحی حمایت/مقاومت",
  bot_analysis: "تحلیل ربات",
  confidence: "اطمینان",
  reasoning: "استدلال",
  tp_label: "حد سود",
  sl_label: "حد ضرر",
  risk_reward: "ریسک : سود",
  levels_from_position: "سطوح زنده از پوزیشن باز شما",
  levels_suggested: "سطوح پیشنهادی · توصیه مالی نیست",
  toast_opened: "پوزیشن {symbol} با اهرم {leverage}x باز شد",
  toast_closed: "{symbol} بسته شد · سود/زیان ${pnl}",
  toast_closed_pnl: "بسته شد · سود/زیان ${pnl}",
  ago_s: "{n} ثانیه پیش",
  ago_m: "{n} دقیقه پیش",
  ago_h: "{n} ساعت پیش",
  ago_d: "{n} روز پیش",
  in_label: "ورودی",
  out_label: "خروجی",
  confirm_reveal_email: "ایمیل این کاربر نمایش داده شود؟ ایمیل‌ها خصوصی هستند — فقط در صورت لزوم ببینید.",
  view_email_private: "مشاهده ایمیل (خصوصی)",
  display_name_updated: "نام نمایشی به‌روزرسانی شد",
  change_email_sent: "لینک تأیید به ایمیل جدید ارسال شد",
  username_updated: "نام کاربری به‌روزرسانی شد",
  password_changed: "رمز عبور تغییر کرد. در دستگاه‌های دیگر دوباره وارد شوید.",
  confirm_logout_all: "از همه دستگاه‌ها خارج شوید؟ باید دوباره وارد شوید.",
  no_wallet: "افزونه کیف پولی شناسایی نشد (MetaMask/WalletConnect).",
  wallet_rejected: "اتصال کیف پول رد شد.",
  profile_security: "پروفایل و امنیت",
  display_name: "نام نمایشی",
  display_name_optional: "نام نمایشی (اختیاری)",
  edit: "ویرایش",
  cancel: "انصراف",
  change: "تغییر",
  in_days: "تا {days} روز دیگر",
  username_label: "نام کاربری",
  username_hint: "3 تا 20 حرف، عدد یا زیرخط. هر 30 روز یک‌بار قابل تغییر.",
  username_example: "مثلاً arash_king",
  email_private: "ایمیل (خصوصی)",
  change_email: "تغییر ایمیل",
  unverified: "تأیید نشده",
  resend_verification: "ارسال مجدد تأیید",
  new_email: "ایمیل جدید",
  current_password: "رمز عبور فعلی",
  new_password: "رمز عبور جدید",
  confirm_new_password: "تکرار رمز عبور جدید",
  send_verification_new: "ارسال تأیید به ایمیل جدید",
  change_email_hint: "از طریق لینکی که به ایمیل جدید ارسال می‌شود تأیید می‌کنید، سپس دوباره وارد شوید.",
  email_private_hint: "ایمیل شما هرگز به‌صورت عمومی نمایش داده نمی‌شود. فقط خودتان می‌توانید آن را اینجا ببینید.",
  sessions: "نشست‌ها",
  logout_all_devices: "خروج از همه دستگاه‌ها",
  change_password: "تغییر رمز عبور",
  pw_hint: "حداقل 8 کاراکتر، یک حرف و یک عدد.",
  update_password: "به‌روزرسانی رمز عبور",
  coin_example_placeholder: "مثلاً BTC، PEPE، TAO...",
  username_or_email: "نام کاربری یا ایمیل",
  forgot_password_q: "رمز عبور را فراموش کرده‌اید؟",
  invalid_email: "یک ایمیل معتبر وارد کنید",
  pw_mismatch: "رمزهای عبور مطابقت ندارند",
  username_hint_short: "3 تا 20 حرف، عدد یا زیرخط",
  account_created_verify: "حساب ساخته شد! برای تأیید حساب، ایمیل خود را بررسی کنید.",
  confirm_password_label: "تکرار رمز عبور",
  missing_verify_token: "توکن تأیید وجود ندارد.",
  email_verified_msg: "ایمیل تأیید شد!",
  verification_failed: "تأیید ناموفق بود.",
  verifying_email: "در حال تأیید ایمیل شما…",
  email_verified_title: "ایمیل تأیید شد",
  go_to_login: "رفتن به صفحه ورود",
  verification_failed_title: "تأیید ناموفق بود",
  verify_expired_hint: "لینک منقضی شده؟ وارد شوید و از «ارسال مجدد تأیید» در تنظیمات استفاده کنید.",
  back_to_login: "← بازگشت به ورود",
  forgot_password: "فراموشی رمز عبور",
  forgot_subtitle: "نام کاربری یا ایمیل خود را وارد کنید تا لینک بازیابی ارسال شود.",
  forgot_sent_toast: "اگر حساب وجود داشته باشد، لینک بازیابی ارسال شد.",
  forgot_sent_full: "اگر حساب وجود داشته باشد، لینک بازیابی ارسال شد. ایمیل خود را بررسی کنید (اعتبار 15 دقیقه).",
  send_reset_link: "ارسال لینک بازیابی",
  remembered_it: "به یاد آوردید؟",
  missing_reset_token: "توکن بازیابی وجود ندارد. از لینک داخل ایمیل خود استفاده کنید.",
  pw_requirement: "رمز عبور باید حداقل 8 کاراکتر و شامل یک حرف و یک عدد باشد",
  password_updated: "رمز عبور به‌روزرسانی شد.",
  reset_password: "بازیابی رمز عبور",
  reset_subtitle: "یک رمز عبور جدید برای حساب خود انتخاب کنید.",
  reset_invalid: "لینک بازیابی نامعتبر یا ناقص است. یک لینک جدید درخواست کنید.",
  request_reset_link: "درخواست لینک بازیابی",
  new_password_label: "رمز عبور جدید",
  pw_hint2: "حداقل 8 کاراکتر شامل یک حرف و یک عدد.",
  update_password_btn: "به‌روزرسانی رمز عبور",
  bullish: "صعودی",
  bearish: "نزولی",
  inflow: "ورودی",
  outflow: "خروجی",
  volume_up: "حجم صعودی",
  volume_down: "حجم نزولی",
  money_flow_mfi: "جریان پول (MFI 14)",
  flow_overbought: "اشباع خرید",
  flow_oversold: "اشباع فروش",
  flow_bullish: "ورود پول صعودی",
  flow_bearish: "خروج پول نزولی",
  support: "حمایت",
  resistance: "مقاومت",
  available_for_trade: "موجودی قابل معامله",
  free_balance_after: "موجودی آزاد پس از معامله",
  tab_major: "میجر",
  tab_shitcoins: "شیت‌کوین",
  tab_nfts: "NFTها",
  filter_direction: "جهت",
  buy_only: "فقط خرید",
  sell_only: "فقط فروش",
  min_confidence: "حداقل اطمینان",
  confirm: "تأیید",
  safety_score: "امتیاز امنیت",
  liquidity_lock: "قفل نقدینگی",
  locked: "قفل‌شده",
  unlocked: "باز",
  honeypot: "هانی‌پات",
  honeypot_safe_label: "امن",
  honeypot_risk: "خطر",
  nft_demo_notice: "معامله دمو — هیچ NFT واقعی خریداری نمی‌شود",
  view_on_opensea: "مشاهده در OpenSea",
  volume_24h: "حجم (24 ساعت)",
  nft_position_opened: "پوزیشن NFT باز شد (دمو)",
  crypto_positions_title: "پوزیشن‌های کریپتو و شیت‌کوین",
  nft_positions_title: "پوزیشن‌های NFT",
  min_safety_required: "برای معامله، امتیاز امنیت باید ≥ 75 باشد",
  amount_exceeds_balance: "مبلغ از موجودی آزاد شما بیشتر است",
  amount_gt_zero: "مبلغ باید بزرگ‌تر از 0 باشد",
  confirm_trade: "تأیید معامله",
  usd_equiv: "≈",
  nft_badge: "NFT",
  no_nft_positions: "پوزیشن NFT بازی وجود ندارد",
  floor: "کف قیمت",
  rr_short: "ریسک/سود",
  best_marketplace: "بهترین بازار",
  story: "داستان",
  show_more: "نمایش بیشتر",
  show_less: "نمایش کمتر",
  view_chart: "نمایش چارت",
  reason_sma_bull: "SMA9 بالای SMA21 (روند صعودی)",
  reason_sma_bear: "SMA9 زیر SMA21 (روند نزولی)",
  reason_rsi_oversold: "اشباع فروش",
  reason_rsi_overbought: "اشباع خرید",
  reason_rsi_neutral: "خنثی",
  reason_pos_momentum: "مومنتوم مثبت",
  reason_neg_momentum: "مومنتوم منفی",
  reason_insufficient: "داده ناکافی",
  reason_whale_accum: "انباشت نهنگ",
  reason_rising_floor: "کف قیمت رو به افزایش",
  reason_falling_floor: "کف قیمت رو به کاهش",
  reason_high_rarity: "نادر بودن بالا",
  reason_mid_rarity: "نادر بودن متوسط",
  nft_trade_history: "تاریخچه معاملات NFT",
  no_nft_trades: "هنوز معامله NFT بسته‌شده‌ای نیست",
  entry_floor: "کف قیمت ورود",
  exit_floor: "کف قیمت خروج",
  verify_reminder: "ایمیل شما هنوز تأیید نشده — برای فعال‌سازی معامله آن را تأیید کنید.",
  verify_now: "تأیید کن",
  dismiss: "بستن",
  trusted_devices: "نشست‌های فعال · دستگاه‌های واردشده",
  font_size: "اندازه فونت", font_small: "کوچک", font_medium: "متوسط", font_large: "بزرگ",
  sessions_hint: "گوشی‌ها و مرورگرهایی که الان وارد حساب شما هستند (کلیدهای API صرافی نیستند).", unknown_device: "دستگاه ناشناس",
  this_device: "این دستگاه",
  current_session: "فعلی",
  last_active: "آخرین فعالیت",
  revoke: "لغو دسترسی",
  revoke_all_others: "خروج از همه دستگاه‌های دیگر",
  revoke_all_others_done: "از همه دستگاه‌های دیگر خارج شدید",
  no_other_devices: "دستگاه فعال دیگری نیست",
  device_revoked: "دستگاه خارج شد",
};

const TRANSLATIONS = { en: { ...SRV_EN, ...en }, fa: { ...SRV_FA, ...fa }, es, de, fr, zh, ja, ru, pt, tr };

// Non-React translation helpers (used by the API layer for backend messages).
export function translate(lang, key, params) {
  let s = (TRANSLATIONS[lang] || {})[key] || TRANSLATIONS.en[key] || key;
  if (params) for (const k in params) s = s.replace(new RegExp(`\\{${k}\\}`, "g"), params[k]);
  return s;
}
export function translateServerMsg(msg, lang) {
  if (lang === "en") return msg;
  const m = matchServer(msg);
  if (!m) return msg;
  const t = (k, p) => translate(lang, k, p);
  const tv = (v) => { const k = VALUE_KEYS[v] || VALUE_KEYS[String(v).toLowerCase()]; return k ? t(k) : v; };
  return renderKey(m.key, m.params, t, tv);
}
export { renderKey };

// Dynamic backend value → translation key
const VALUE_KEYS = {
  BUY: "buy", SELL: "sell", HOLD: "hold",
  Buy: "buy", Sell: "sell", Hold: "hold",
  manual: "src_manual", auto: "src_auto",
  Legendary: "tier_legendary", Epic: "tier_epic", Rare: "tier_rare", Common: "tier_common",
  Tradeable: "verdict_tradeable", Watch: "verdict_watch",
  accumulation: "pressure_accumulation", distribution: "pressure_distribution",
  take_profit: "reason_take_profit", stop_loss: "reason_stop_loss", liquidation: "reason_liquidation",
  trailing_take_profit: "reason_trailing_take_profit", trailing_stop: "reason_trailing_stop",
  signal_flip: "reason_signal_flip",
  Bullish: "bullish", Bearish: "bearish", bullish: "bullish", bearish: "bearish",
  inflow: "inflow", outflow: "outflow",
  cex: "kind_cex", iranian_cex: "kind_iranian_cex", dex: "kind_dex",
};

// ---- Number formatting: always English digits, LTR ----
const NUM = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
export function fmtNum(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const num = Number(n);
  if (Math.abs(num) < 0.01 && num !== 0) return num.toPrecision(3);
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: d }).format(num);
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("fb_lang") || "en");

  useEffect(() => {
    localStorage.setItem("fb_lang", lang);
    const meta = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
    document.documentElement.dir = meta.dir;
    document.documentElement.lang = lang;
    document.documentElement.classList.toggle("font-fa", lang === "fa");
  }, [lang]);

  const value = useMemo(() => {
    const t = (key, params) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.en;
      let s = dict[key] || TRANSLATIONS.en[key] || key;
      if (params) {
        for (const k in params) s = s.replace(new RegExp(`\\{${k}\\}`, "g"), params[k]);
      }
      return s;
    };
    // translate a dynamic backend value (side, tier, verdict, reason, source)
    const tv = (val) => {
      if (val == null) return val;
      const key = VALUE_KEYS[val] || VALUE_KEYS[String(val).toLowerCase()];
      return key ? t(key) : val;
    };
    const dir = (LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0]).dir;
    return { lang, setLang, t, tv, dir };
  }, [lang]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}

// Translate backend-composed signal reason strings fragment-by-fragment,
// keeping numbers (RSI values, %) intact. `t` comes from useI18n().
export function tReason(reason, t) {
  if (!reason) return reason;
  let s = reason;
  s = s.split("SMA9 above SMA21 (bullish trend)").join(t("reason_sma_bull"));
  s = s.split("SMA9 below SMA21 (bearish trend)").join(t("reason_sma_bear"));
  s = s.split("Insufficient data").join(t("reason_insufficient"));
  s = s.split("Whale accumulation").join(t("reason_whale_accum"));
  s = s.split("Rising floor").join(t("reason_rising_floor"));
  s = s.split("Falling floor").join(t("reason_falling_floor"));
  s = s.split("High rarity").join(t("reason_high_rarity"));
  s = s.split("Mid rarity").join(t("reason_mid_rarity"));
  s = s.split("Positive momentum").join(t("reason_pos_momentum"));
  s = s.split("Negative momentum").join(t("reason_neg_momentum"));
  s = s.replace(/\boversold\b/g, t("reason_rsi_oversold"));
  s = s.replace(/\boverbought\b/g, t("reason_rsi_overbought"));
  s = s.replace(/\bneutral\b/g, t("reason_rsi_neutral"));
  return s;
}
